/**
 * Schema.org JSON-LD generator for SEO.
 * Implemented in lib/wordpress so consumers (e.g. HEN Site) do not depend on supersonic-middleware.
 */

import { parse } from 'node-html-parser';
import type { ConfigProps } from '../configTypes';
import type { EnrichedContent, SeoTransformedContentData } from './types';
import type { SchemaType } from '../configTypes';
import {
  getContributorPath,
  getContributorGroups,
  getContributorsByType,
} from './lib/contributorPaths';
import type { ContributorGroup } from './lib/contributorPaths';

const SCHEMA_CONTEXT = 'https://schema.org';

/** URIs that should use Organization schema instead of Article (shared with apps/site and hen-site). */
export const ORGANIZATION_PAGE_URIS = [
  'contact-us',
  'home',
  'about-us',
  'about',
  'brands',
  'expertise',
] as const;

/** Returns true when the page URI should use Organization schema. */
export function isOrganizationPage(uri: string | undefined): boolean {
  if (uri == null) return false;
  const normalized = uri.replace(/^\/+|\/+$/g, '');
  return (ORGANIZATION_PAGE_URIS as readonly string[]).includes(normalized);
}

/** Data shape passed to generateSchema: post-like object with seo and enrichedContent. */
/** Contributor profile fields for Person schema generation. */
export interface ContributorProfileSchemaData {
  name: string;
  slug?: string;
  bio?: string;
  imageSrc?: string;
  roles?: string[];
  socialMedia?: { key: string; value: string }[];
  highlightFields?: { type: string; items?: string[]; links?: string[] }[];
}

/** Category hub fields for CategoryHub schema generation. */
export interface CategoryHubSchemaData {
  categoryName: string;
  categoryDescription?: string;
  slug: string;
}

export interface SchemaGeneratorData {
  title?: string;
  excerpt?: string;
  content?: string;
  uri?: string;
  date?: string;
  publishedDate?: string;
  seo?: {
    title?: string;
    description?: string;
    url?: string;
    imageUrl?: string;
    imageWidth?: string;
    imageHeight?: string;
    imageType?: string;
    siteName?: string;
    locale?: string;
    aboutType?: string;
    aboutName?: string;
  };
  enrichedContent?: EnrichedContent | null;
  contributors?: { types?: ContributorGroup[] } | unknown[];
  categories?: { edges?: Array<{ node?: { name?: string } }> };
  contributorProfile?: ContributorProfileSchemaData;
  categoryHub?: CategoryHubSchemaData;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getOrganizationSocials(config: ConfigProps): string[] {
  return [
    config.socialFacebookUrl ?? '',
    config.socialInstagramUrl ?? '',
    config.socialLinkedinUrl ?? '',
    config.socialTwitterUrl ?? '',
    config.socialYoutubeUrl ?? '',
  ].filter((s) => s !== '');
}

const SPEAKABLE_LOCALES = ['en-us', 'en_us'];

function cleanText(raw: string): string {
  if (!raw) return '';
  return raw
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/\r?\n+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function normalizeQuotes(raw: string): string {
  return raw
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
    .replace(/&#8220;|&#8221;|&#8222;|&#8223;|&#8243;|&#8246;/g, '"');
}

function buildSpeakableSchema(
  content?: string,
  heroSubtitle?: string,
  pageTitle?: string,
): Record<string, unknown> | null {
  if (!content || content.length === 0) return null;

  const normalizedContent = normalizeQuotes(content);

  const hasH1 = /<h1\b/i.test(normalizedContent);
  const hasHenText = /\[hen-ui-text\b/i.test(normalizedContent);
  const root = parse(normalizedContent);

  const hasTitleAttr = Boolean(root.querySelector('[data-speakable-title]'));
  const hasDescAttr = Boolean(
    root.querySelector('[data-speakable-description]'),
  );

  const seoDataDescPattern =
    /\[hen-seo-data[^\]]*role="speakable-description"[^\]]*\]([\s\S]*?)\[\/hen-seo-data\]/i;
  const seoDataDescMatch = normalizedContent.match(seoDataDescPattern);
  const hasShortcodeDesc = Boolean(seoDataDescMatch?.[1]?.trim());

  const hasHeroSubtitle = Boolean(heroSubtitle?.trim());

  if (
    !hasTitleAttr &&
    !hasDescAttr &&
    !hasShortcodeDesc &&
    !hasHeroSubtitle &&
    !hasH1 &&
    !hasHenText
  )
    return null;

  const textFrom = (selector: string): string | undefined => {
    const el = root.querySelector(selector);
    const cleaned = cleanText(el?.innerText ?? '');
    return cleaned || undefined;
  };

  const textFromShortcodeDesc = (): string | undefined => {
    if (!seoDataDescMatch?.[1]) return undefined;
    const raw = seoDataDescMatch[1]
      .replace(/<[^>]+>/g, '')
      .replace(/\[\/?[^\]]+\]/g, '');
    return cleanText(raw) || undefined;
  };

  const firstShortcodeText = (): string | undefined => {
    const m = normalizedContent.match(
      /\[hen-ui-text[^\]]*\]([\s\S]*?)\[\/hen-ui-text\]/i,
    );
    if (m?.[1]) {
      const cleaned = cleanText(m[1].replace(/<[^>]+>/g, ''));
      if (cleaned) return cleaned;
    }
    const p = root.querySelector('p');
    return p ? cleanText(p.innerText ?? '') || undefined : undefined;
  };

  const cssSelector: string[] = ['h1'];
  if (hasDescAttr) cssSelector.push('[data-speakable-description]');

  let titleText: string | undefined;
  if (hasTitleAttr) titleText = textFrom('[data-speakable-title]');
  else if (hasH1) titleText = textFrom('h1');
  else if (hasHeroSubtitle) titleText = cleanText(heroSubtitle!);
  else if (pageTitle) titleText = cleanText(pageTitle);

  let descText: string | undefined;
  if (hasDescAttr) descText = textFrom('[data-speakable-description]');
  else if (hasShortcodeDesc) descText = textFromShortcodeDesc();
  else if (hasHeroSubtitle) descText = cleanText(heroSubtitle!);
  else if (hasH1) descText = titleText ?? textFrom('h1');
  else descText = firstShortcodeText();

  let description: string | undefined;
  if (titleText && descText && titleText !== descText) {
    const separator = titleText.endsWith('.') ? ' ' : '. ';
    description = `${titleText}${separator}${descText}`;
  } else {
    description = titleText || descText;
  }

  return {
    '@type': 'SpeakableSpecification',
    cssSelector,
    ...(description ? { description } : {}),
  };
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

/**
 * Builds a single schema.org JSON-LD object for the given type.
 */
export function generateSchema(
  data: SchemaGeneratorData,
  schemaType: SchemaType,
  config: ConfigProps,
): Record<string, unknown> {
  const baseUrl = config.siteUrl || '';
  const url =
    data.seo?.url ||
    (data.uri ? `${baseUrl}/${(data.uri as string).replace(/^\//, '')}` : '');
  const title = data.seo?.title || data.title || config.name || '';
  const description = data.seo?.description || data.excerpt || '';

  switch (schemaType) {
    case 'Organization': {
      const logoUrl = config.logoUrl || config.siteLogoUrl;
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Organization',
        name: config.name || config.domainLabelWithExtension || title,
        ...(description ? { description } : {}),
        ...(logoUrl ? { logo: logoUrl } : {}),
        url: url || `${baseUrl}/`,
      };
    }

    case 'Article': {
      const contributorPath = getContributorPath(config);
      const groups = getContributorGroups(data.contributors);
      const locale = data.seo?.locale ?? '';
      const hero = data['hero'] as { subtitle?: string } | undefined;
      const speakable = SPEAKABLE_LOCALES.includes(locale.toLowerCase())
        ? buildSpeakableSchema(data.content, hero?.subtitle, data.title)
        : null;

      const articleSection =
        data.categories?.edges
          ?.map((e) => e.node?.name ?? '')
          .filter((n) => n !== '') ?? [];

      const authors = getContributorsByType(groups, 'writer').map((c) => ({
        '@type': 'Person',
        name: c.title,
        url: baseUrl + contributorPath + (c.slug ?? ''),
      }));

      const editors = getContributorsByType(groups, 'editor').map((c) => ({
        '@type': 'Person',
        name: c.title,
        url: baseUrl + contributorPath + (c.slug ?? ''),
      }));

      const logoUrl = config.logoUrl || config.siteLogoUrl;
      const sameAs = getOrganizationSocials(config);
      const imageUrl = data.seo?.imageUrl;

      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Article',
        ...(url
          ? { mainEntityOfPage: { '@type': 'WebPage', '@id': url } }
          : {}),
        headline: title,
        description: description || undefined,
        ...(imageUrl ? { image: imageUrl } : {}),
        articleSection,
        author: authors,
        editor: editors,
        publisher: {
          '@type': 'Organization',
          name: config.name,
          url: baseUrl,
          ...(logoUrl
            ? { logo: { '@type': 'ImageObject', url: logoUrl } }
            : {}),
          ...(sameAs.length ? { sameAs } : {}),
        },
        ...(locale ? { inLanguage: locale } : {}),
        ...(data.publishedDate ? { datePublished: data.publishedDate } : {}),
        ...(data.date ? { dateModified: data.date } : {}),
        ...(speakable ? { speakable } : {}),
        ...(data.seo?.aboutType && data.seo?.aboutName
          ? {
              about: {
                '@type': data.seo.aboutType,
                name: data.seo.aboutName,
              },
            }
          : {}),
      };
    }

    case 'ItemList': {
      const rankings = data.enrichedContent?.rankings
        ? Object.values(data.enrichedContent.rankings)[0]
        : null;
      const schools =
        rankings && typeof rankings === 'object' && 'schools' in rankings
          ? (rankings as { schools: unknown[] }).schools
          : [];
      const itemListElement = (Array.isArray(schools) ? schools : []).map(
        (item: unknown, index: number) => {
          const s = item as {
            title?: string;
            link?: string;
            name?: string;
            href?: string;
            overrideFields?: {
              schoolName?: string;
              schoolUrl?: string;
              programName?: string;
              programUrl?: string;
            };
            details?: {
              schoolName?: string;
              schoolUrl?: string;
              programName?: string;
              programUrl?: string;
            };
          };
          const override = s.overrideFields ?? {};
          const details = s.details ?? {};
          const itemName =
            s.title ||
            s.name ||
            override.schoolName ||
            details.schoolName ||
            '';
          const itemUrl =
            s.link ||
            s.href ||
            override.programUrl ||
            override.schoolUrl ||
            details.programUrl ||
            details.schoolUrl;
          return {
            '@type': 'ListItem',
            position: index + 1,
            ...(itemUrl
              ? {
                  item: {
                    '@type': 'CollegeOrUniversity',
                    '@id': itemUrl,
                    name: itemName,
                    url: itemUrl,
                  },
                }
              : { name: itemName }),
          };
        },
      );
      const hero = data['hero'] as { subtitle?: string } | undefined;
      const itemListDescription =
        hero?.subtitle?.trim() || description || undefined;
      const itemListName = data.title || title;
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'ItemList',
        name: itemListName,
        description: itemListDescription,
        itemListOrder: 'Ascending',
        numberOfItems: itemListElement.length,
        itemListElement,
      };
    }

    case 'FAQPage': {
      const seoData = data.enrichedContent?.seoTransformedContentData as
        | SeoTransformedContentData
        | undefined;
      const questions = seoData?.faqPage?.questions ?? [];
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'FAQPage',
        mainEntity: questions.map((q) => ({
          '@type': 'Question',
          name: q.text,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer ?? '',
          },
        })),
      };
    }

    case 'HowTo': {
      const seoData = data.enrichedContent?.seoTransformedContentData as
        | SeoTransformedContentData
        | undefined;
      const howTo = seoData?.howToPage;
      if (!howTo?.steps?.length) {
        return { '@context': SCHEMA_CONTEXT, '@type': 'HowTo', name: title };
      }
      const howToSchema: Record<string, unknown> = {
        '@context': SCHEMA_CONTEXT,
        '@type': 'HowTo',
        ...(url ? { '@id': url } : {}),
        name: howTo.title || title,
        step: howTo.steps.map((step, i) => ({
          '@type': 'HowToStep',
          position: i + 1,
          text: step.text || step.answer,
        })),
      };
      if (howTo.description) howToSchema['description'] = howTo.description;
      if (howTo.totalTime) howToSchema['totalTime'] = howTo.totalTime;
      if (howTo.estimatedCost) {
        howToSchema['estimatedCost'] = {
          '@type': 'MonetaryAmount',
          currency: howTo.estimatedCost.currency || 'USD',
          value: howTo.estimatedCost.value,
        };
      }
      return howToSchema;
    }

    case 'Occupation': {
      const seoData = data.enrichedContent?.seoTransformedContentData as
        | SeoTransformedContentData
        | undefined;
      const occ = seoData?.occupation;
      if (!occ?.name) {
        return {
          '@context': SCHEMA_CONTEXT,
          '@type': 'Occupation',
          name: title,
        };
      }
      const occSchema: Record<string, unknown> = {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Occupation',
        name: occ.name,
      };
      if (occ.alternateName) occSchema['alternateName'] = occ.alternateName;
      if (occ.description) occSchema['description'] = occ.description;
      if (occ.occupationalCategory)
        occSchema['occupationalCategory'] = occ.occupationalCategory;
      if (occ.occupationLocationName)
        occSchema['occupationLocationName'] = occ.occupationLocationName;
      if (occ.educationRequirements)
        occSchema['educationRequirements'] = occ.educationRequirements;
      if (occ.experienceRequirements)
        occSchema['experienceRequirements'] = occ.experienceRequirements;
      if (occ.skills) occSchema['skills'] = occ.skills;
      if (occ.occupationalOutlook)
        occSchema['occupationalOutlook'] = occ.occupationalOutlook;
      if (occ.estimatedSalary)
        occSchema['estimatedSalary'] = occ.estimatedSalary;
      return occSchema;
    }

    case 'Person': {
      const firstContributor = Array.isArray(data.contributors)
        ? (data.contributors[0] as {
            name?: string;
            title?: string;
            jobTitle?: string;
          })
        : null;
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Person',
        name: firstContributor?.name || title,
        jobTitle: firstContributor?.jobTitle || firstContributor?.title,
      };
    }

    case 'ContributorHub': {
      const contributorPath = getContributorPath(config);
      const orgLogoUrl = config.logoUrl || config.siteLogoUrl;
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Article',
        headline: 'Contributors',
        description: 'Contributors',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${baseUrl}${contributorPath}`,
        },
        publisher: {
          '@type': 'Organization',
          name: config.name,
          url: baseUrl,
          ...(orgLogoUrl
            ? { logo: { '@type': 'ImageObject', url: orgLogoUrl } }
            : {}),
        },
      };
    }

    case 'ContributorProfile': {
      const profile = data.contributorProfile;
      if (!profile) {
        return { '@context': SCHEMA_CONTEXT, '@type': 'Person', name: title };
      }
      const contribPath = getContributorPath(config);
      const profileUrl = `${baseUrl}${contribPath}${profile.slug || ''}`;
      const profLogoUrl = config.logoUrl || config.siteLogoUrl;

      const bio = profile.bio
        ? profile.bio
            .replace(/\[\/?hen-ui-[^\]]*\]/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim()
        : undefined;

      const sameAs = (profile.socialMedia ?? [])
        .map((s) => s.value?.trim())
        .filter((v): v is string => !!v && /^https?:\/\//.test(v));

      const alumniOf = (profile.highlightFields ?? [])
        .filter((f) => /education/i.test(f.type))
        .flatMap((f) => f.items ?? []);

      const knowsAbout = (profile.highlightFields ?? [])
        .filter((f) => /expertise|subject/i.test(f.type))
        .flatMap((f) => f.items ?? []);

      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Person',
        name: profile.name,
        url: profileUrl,
        ...(bio ? { description: bio } : {}),
        ...(profile.imageSrc ? { image: profile.imageSrc } : {}),
        ...(profile.roles?.length ? { jobTitle: profile.roles } : {}),
        ...(alumniOf.length ? { alumniOf } : {}),
        ...(knowsAbout.length ? { knowsAbout } : {}),
        ...(sameAs.length ? { sameAs } : {}),
        worksFor: {
          '@type': 'Organization',
          name: config.name,
          url: baseUrl,
          ...(profLogoUrl ? { logo: profLogoUrl } : {}),
        },
      };
    }

    case 'CategoryHub': {
      const cat = data.categoryHub;
      const catLogoUrl = config.logoUrl || config.siteLogoUrl;
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Article',
        headline: cat?.categoryName || title,
        ...(cat?.categoryDescription
          ? { description: cat.categoryDescription }
          : {}),
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${baseUrl}/${cat?.slug || ''}/`,
        },
        publisher: {
          '@type': 'Organization',
          name: config.name,
          url: baseUrl,
          ...(catLogoUrl
            ? { logo: { '@type': 'ImageObject', url: catLogoUrl } }
            : {}),
        },
      };
    }

    default:
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': schemaType,
        name: title,
        description: description || undefined,
      };
  }
}
